// Import React & core libraries
import React from 'react'
import ReactDOM from 'react-dom'
import { useSelector } from 'react-redux'

// Import necessary styles and CSS
import '../../App.css'

/**
 * AlertMessage Component
 *
 * A reusable alert dialog that displays a message, optional subtext, and one or two buttons.
 *
 * Props:
 * - message (string): The main alert message.
 * - subtext (string, optional): Additional explanatory text.
 * - onConfirm (function): Function called when the confirm/OK button is clicked.
 * - onCancel (function, optional): Function called when the cancel button is clicked. If provided, displays "Yes/No" buttons instead of a single "OK" button.
 */
const AlertMessage = ({
  message,
  subtext,
  onConfirm,
  onCancel,
  confirmButtonText,
}) => {
  const language = useSelector((state) => state.language)
  const translations = useSelector((state) => state.translationsData)

  const getTranslationText = (
    keyPath,
    data = translations,
    lang = language.toUpperCase(),
  ) => {
    const keys = keyPath.split('.')
    let result = data
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i]
      if (Array.isArray(result)) {
        const found = result.find((item) => item.label === key)
        if (!found) return undefined
        result = found
      } else if (result[key] === undefined) {
        return undefined
      } else {
        result = result[key]
      }
    }
    if (
      typeof result === 'object' &&
      !Array.isArray(result) &&
      result[lang] !== undefined
    ) {
      return result[lang]
    }
    return result
  }

  const yesButton =
    getTranslationText(
      'authorization.termsAndConditionsConsent.yesButtons.text',
    ) || 'Yes'
  const noButton =
    getTranslationText(
      'authorization.termsAndConditionsConsent.noButtons.text',
    ) || 'No'

  // ── Portal renders directly into document.body, escaping ALL parent
  //    stacking contexts (flex containers, overflow:hidden, z-index trees)
  return ReactDOM.createPortal(
    <div className='alert-overlay'>
      <div className='alert-box'>
        {/* Main alert message */}
        <p className='alert-message'>{message}</p>

        {/* Optional subtext */}
        {subtext && <p className='alert-subtext'>{subtext}</p>}

        {/* Divider */}
        <div className='alert-divider'></div>

        {/* Buttons */}
        <div className='alert-buttons'>
          {onCancel ? (
            <>
              <button
                type='button'
                className='alert-button cancel'
                onClick={() => {
                  onCancel && onCancel()
                }}
              >
                {noButton}
              </button>
              <button
                type='button'
                className='alert-button confirm'
                onClick={() => {
                  onConfirm && onConfirm()
                }}
              >
                {yesButton}
              </button>
            </>
          ) : (
            <button className='alert-button ok' onClick={onConfirm}>
              {confirmButtonText || 'OK'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body, // ← renders outside every React tree into <body> directly
  )
}

export default AlertMessage
